import { Loader } from '@southfields-digital/mpxlive-components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Socket } from 'socket.io-client';

import { LAYOUTS } from 'src/config/layouts';
import RundownLocked from 'src/components/RundownLocked';
import { injectBaseLayoutContext, InjectedBaseLayoutContextProps } from 'src/layouts/BaseLayout';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_GET_MATCH_CONTROL,
  REQUEST_GET_MATCH_CONTROL_CONTROLS,
} from 'src/redux/reducers/matchControls';
import { REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID } from 'src/redux/reducers/playlist';
import { REQUEST_GET_ALL_PLAYLIST_GRAPHICS } from 'src/redux/reducers/playlistGraphic';
import { actions as bundledPlaylistGraphicActions } from 'src/redux/reducers/bundledPlaylistGraphic';
import { REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS } from 'src/redux/reducers/playlistDisplayHub';
import {
  REQUEST_GET_RUNDOWN,
  REQUEST_UPDATE_RUNDOWN_OPERATOR,
  RundownDetailStateType,
  UPDATE_CURRENT_RUNDOWN,
} from 'src/redux/reducers/rundown';
import { RECEIVE_MOS_STATUS } from 'src/redux/reducers/mos';
import { WebsocketBuddy } from 'src/services/websockets';
import { NOTIFY } from 'src/redux/reducers/notification';
import { Variant } from 'src/components/Toast/types';
import { useRundownLayout } from 'src/components/RundownSettings/useRundownLayout';
import {
  ISocketMatchControlProps,
  ISocketPlaylistGraphicProps,
  ISocketPlaylistProps,
  ISocketRundownProps,
} from 'src/types/socket/socket';

import styles from './RundownDetail.module.scss';

function RundownDetail({
  baseLayoutContext: {
    setCurrentlyOpenRundownId,
    setFullWidth,
    setDisplayClock,
    setGoLive,
    setShowRundownName,
    setShowRundownSettings,
  },
}: InjectedBaseLayoutContextProps) {
  const socketInstance = useRef<Socket | null>(null);
  const { rundownId } = useParams<{ rundownId: string }>();
  const { layout } = useRundownLayout();

  const dispatch = useDispatch();

  const { data, loading, error }: RundownDetailStateType = useSelector(
    (state: StateType) => state.rundown.detail
  );

  const [overwriteLockedWindow, setOverwriteLockedWindow] = useState<boolean>(false);

  const getRundown = (rundownId: string | null) => {
    dispatch({ type: REQUEST_GET_RUNDOWN, payload: { id: rundownId } });
  };

  const getRundownGraphics = (rundownId: string) => {
    dispatch({ type: REQUEST_GET_ALL_PLAYLIST_GRAPHICS, payload: { rundownId } });
  };

  const getBundledGraphics = (rundownId: string) => {
    dispatch(bundledPlaylistGraphicActions.requestGetBundledPlaylistGraphics({ rundownId }));
  };

  const getRundownDisplayHubs = (rundownId: string) => {
    dispatch({ type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS, payload: { rundownId } });
  };

  const handleSocketRundown = (_: ISocketRundownProps) => {
    dispatch({
      type: UPDATE_CURRENT_RUNDOWN,
      payload: { id: rundownId },
    });
  };

  const handleSocketPlaylist = (_: ISocketPlaylistProps) => {
    dispatch({
      type: REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
      payload: { rundownId, resetSelected: false },
    });
  };

  const handleSocketPlaylistGraphic = (_: ISocketPlaylistGraphicProps) => {
    if (rundownId) {
      getRundownGraphics(rundownId);
      getBundledGraphics(rundownId);
    }
  };

  const handleSocketPlaylistDisplayHub = (_: ISocketPlaylistGraphicProps) => {
    if (rundownId) {
      getRundownDisplayHubs(rundownId);
    }
  };

  const handleSocketMatchControl = (data: ISocketMatchControlProps) => {
    dispatch({ type: REQUEST_GET_MATCH_CONTROL, payload: { rundownId } });
    dispatch({ type: REQUEST_GET_MATCH_CONTROL_CONTROLS, payload: { rundownId } });

    if (data.clockType === 'penaltyclock') {
      const variant: Variant = data.clockBaseTime <= 120000 ? 'success' : 'warning';
      dispatch({ type: NOTIFY, payload: { variant, title: 'Penalty clock added' } });
    }
  };

  // Keeps all users in sync when someone toggles MOS auto-fetch.
  const handleSocketMosStatus = (data: { autoFetch: boolean; connected: boolean }) => {
    dispatch({ type: RECEIVE_MOS_STATUS, payload: data });
  };

  const handleUpdateRundownOperator = () => {
    dispatch({ type: REQUEST_UPDATE_RUNDOWN_OPERATOR, payload: { id: rundownId } });
  };

  useEffect(() => {
    if (!rundownId) return;
    getRundown(rundownId);
    getRundownGraphics(rundownId);
    getBundledGraphics(rundownId);
    getRundownDisplayHubs(rundownId);
  }, [rundownId, dispatch]);

  useEffect(() => {
    setCurrentlyOpenRundownId(rundownId);
    setFullWidth(true);
    setGoLive(true);
    setShowRundownName(true);
    setShowRundownSettings(true);
    setDisplayClock(true);

    return () => {
      setCurrentlyOpenRundownId(undefined);
      setFullWidth(false);
      setGoLive(false);
      setDisplayClock(false);
      setShowRundownName(false);
      setShowRundownSettings(false);

      if (socketInstance.current) {
        socketInstance.current.disconnect();
        socketInstance.current = null;
      }

      getRundown(null);
    };
  }, []);

  useEffect(() => {
    if (!data) return;

    if (data.sandboxChannelId && !socketInstance.current) {
      const { socket } = WebsocketBuddy({
        channel: data.sandboxChannelId,
        driver: 'SocketIo',
        listeners: {
          rundown: handleSocketRundown,
          playlist: handleSocketPlaylist,
          playlistGraphic: handleSocketPlaylistGraphic,
          playlistDisplayHub: handleSocketPlaylistDisplayHub,
          matchControl: handleSocketMatchControl,
          mosStatus: handleSocketMosStatus,
        },
      });

      socketInstance.current = socket;
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const showHeaders = !data.isLocked || overwriteLockedWindow;
    setGoLive(showHeaders && !overwriteLockedWindow);
    setShowRundownName(showHeaders);
    setShowRundownSettings(showHeaders);
    setDisplayClock(showHeaders);
  }, [data?.isLocked, overwriteLockedWindow]);

  if (!rundownId || loading) return <Loader centeredFullscreen />;
  if (error) return <div className={styles.CenteredContent}>{error}</div>;
  if (!data) return <div className={styles.CenteredContent}>No Rundown found</div>;
  if (data.isLocked && data.usedBy && !overwriteLockedWindow) {
    return (
      <RundownLocked
        data={data.usedBy}
        handleTakeOver={handleUpdateRundownOperator}
        handleAssistant={() => setOverwriteLockedWindow(true)}
      />
    );
  }

  const Layout = (LAYOUTS.find((l) => l.key === (layout ?? 'default')) ?? LAYOUTS[0]).component;
  return <Layout />;
}

export default injectBaseLayoutContext(RundownDetail);
